<script>
  import { onMount, onDestroy } from 'svelte';
  import { t } from '../i18n/index.js';
  import { showScreen } from '../stores/screen.js';
  import { savePairingInfo } from '../stores/session.js';
  import { ensurePersistentPairing, confirmPendingUploads } from '../pairing.js';
  import { uploadQueue } from '../upload-queue.js';

  let qrScanner = null;
  let errorMessage = $state('');
  let qrReaderElement;

  const API_BASE = import.meta.env.VITE_API_BASE || 'https://api.scantodesk.app';

  onMount(async () => {
    await startQRScanner();
  });

  onDestroy(() => {
    stopQRScanner();
  });

  async function startQRScanner() {
    try {
      // Clean up previous scanner
      await stopQRScanner();

      qrScanner = new window.Html5Qrcode('qr-reader');

      const config = {
        fps: 15,
        qrbox: (viewfinderWidth, viewfinderHeight) => {
          const size = Math.min(viewfinderWidth, viewfinderHeight) * 0.7;
          return { width: size, height: size };
        },
        formatsToSupport: [window.Html5QrcodeSupportedFormats.QR_CODE]
      };

      await qrScanner.start(
        { facingMode: 'environment' },
        config,
        handleQRCode,
        () => {} // Ignore scan failures
      );

      errorMessage = '';
    } catch (e) {
      console.error('Failed to start QR scanner:', e);
      errorMessage = t('pairing.error.cameraNotAllowed');
    }
  }

  async function stopQRScanner() {
    if (qrScanner) {
      try {
        const state = qrScanner.getState();
        if (state === window.Html5QrcodeScannerState.SCANNING) {
          await qrScanner.stop();
        }
      } catch (e) {
        // Ignore
      }
      qrScanner = null;
    }
  }

  async function handleQRCode(decodedText) {
    try {
      await stopQRScanner();

      // Check if URL format
      if (decodedText.startsWith('http://') || decodedText.startsWith('https://')) {
        const url = new URL(decodedText);

        // New format: /s/{code}
        const codeMatch = url.pathname.match(/^\/s\/([A-HJ-NP-Z2-9]{7})$/i);
        if (codeMatch) {
          const code = codeMatch[1].toUpperCase();
          console.log('QR short code detected:', code);

          const response = await fetch(`${API_BASE}/s/${code}`);
          const data = await response.json();

          if (!data.valid) {
            if (data.error === 'code_expired') {
              throw new Error(t('pairing.error.codeExpired'));
            } else if (data.error === 'code_not_found') {
              throw new Error(t('pairing.error.codeNotFound'));
            } else if (data.error === 'code_invalid') {
              throw new Error(t('pairing.error.codeInvalid'));
            }
            throw new Error(t('pairing.error.connectionFailed'));
          }

          // Confirm before overwriting if pending uploads exist
          if (!confirmPendingUploads()) {
            startQRScanner();
            return;
          }

          const pairingInfo = {
            device_id: data.sessionId,
            endpoint: data.endpoint,
            credentials: { apiKey: data.apiKey },
            desktop_name: data.deviceName || null,
            desktop_fingerprint: data.fingerprint || null,
            paired_at: new Date().toISOString()
          };

          uploadQueue.clear();
          if (savePairingInfo(pairingInfo)) {
            // Create persistent pairing if desktop fingerprint is available
            if (pairingInfo.desktop_fingerprint) {
              ensurePersistentPairing(pairingInfo);
            }
            showScreen('camera');
            return;
          }
          throw new Error(t('pairing.error.saveFailed'));
        }

        // Legacy format: ?data= parameter
        const encoded = url.searchParams.get('data');
        if (encoded) {
          const decoded = atob(decodeURIComponent(encoded));
          const data = JSON.parse(decoded);

          if (!data.device_id || !data.endpoint || !data.credentials?.apiKey) {
            throw new Error(t('pairing.error.invalidQr'));
          }

          // Confirm before overwriting if pending uploads exist
          if (!confirmPendingUploads()) {
            startQRScanner();
            return;
          }

          const pairingInfo = {
            device_id: data.device_id,
            endpoint: data.endpoint,
            credentials: data.credentials,
            paired_at: new Date().toISOString()
          };

          uploadQueue.clear();
          if (savePairingInfo(pairingInfo)) {
            showScreen('camera');
            return;
          }
          throw new Error(t('pairing.error.saveFailed'));
        }

        throw new Error(t('pairing.error.noData'));
      }

      // Legacy: direct JSON
      const data = JSON.parse(decodedText);
      if (!data.device_id || !data.endpoint || !data.credentials?.apiKey) {
        throw new Error(t('pairing.error.invalidQr'));
      }

      // Confirm before overwriting if pending uploads exist
      if (!confirmPendingUploads()) {
        startQRScanner();
        return;
      }

      const pairingInfo = {
        device_id: data.device_id,
        endpoint: data.endpoint,
        credentials: data.credentials,
        paired_at: new Date().toISOString()
      };

      uploadQueue.clear();
      if (savePairingInfo(pairingInfo)) {
        showScreen('camera');
        return;
      }
      throw new Error(t('pairing.error.saveFailed'));

    } catch (e) {
      console.error('QR code processing failed:', e);

      // Network errors (fetch failed, no internet) show friendly message
      if (e instanceof TypeError && e.message === 'Failed to fetch') {
        errorMessage = t('pairing.error.connectionFailed');
      } else {
        errorMessage = e.message || t('pairing.error.connectionFailed');
      }

      // Restart scanner after error
      setTimeout(() => {
        startQRScanner();
      }, 2000);
    }
  }
</script>

<div class="screen flex flex-col h-full">
  <header class="p-4 text-center">
    <h1 class="text-2xl font-bold">{t('pairing.title')}</h1>
    <p class="text-gray-400 mt-2">{t('pairing.subtitle')}</p>
  </header>

  <main class="flex-1 flex flex-col items-center justify-center p-4">
    <div id="qr-reader" class="qr-reader mb-4" bind:this={qrReaderElement}></div>
    <p class="text-gray-400 text-center mt-4">
      {t('pairing.instruction')}
    </p>

    {#if errorMessage}
      <p class="error-message text-center mt-4">{errorMessage}</p>
    {/if}
  </main>
</div>

<style>
  .screen {
    display: flex;
  }

  .qr-reader {
    width: 100%;
    max-width: 400px;
  }

  .qr-reader :global(video) {
    border-radius: 0.5rem;
  }

  .error-message {
    color: #f87171;
    font-size: 1rem;
    line-height: 1.5;
    white-space: pre-line;
    padding: 0.75rem 1rem;
    background: rgba(248, 113, 113, 0.1);
    border-radius: 0.5rem;
    max-width: 320px;
  }
</style>
